'use client';

import { trackEvent } from '@tuic/tracking';
import { useEffect, useRef } from 'react';

import type { TelemetryInstruction } from '@modules/prism/versions/2025-07-01/utils/telemetry/types.api';

/**
 * Hook that automatically fires layer (dialog) telemetry based on open state
 *
 * Fires onLayerOpen telemetry when the dialog opens, and onLayerClose when it closes.
 * Only fires on actual open/close transitions, not on telemetry data updates.
 *
 * @param telemetry - Array of telemetry instructions from the API
 * @param open - Current open state of the dialog
 *
 * @example
 * ```tsx
 * function MyDialog({ config, open, onClose }) {
 *   useDialogTelemetry(config.telemetry, open);
 *
 *   return (
 *     <Dialog open={open} onClose={onClose}>
 *       {config.content}
 *     </Dialog>
 *   );
 * }
 * ```
 */
export function useDialogTelemetry(
  telemetry: TelemetryInstruction[] | undefined,
  open: boolean,
): void {
  const prevOpenRef = useRef(open);

  useEffect(() => {
    const prevOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && !prevOpen) {
      fireLayerTelemetry(telemetry, 'onLayerOpen');
      return;
    }

    if (!open && prevOpen) {
      fireLayerTelemetry(telemetry, 'onLayerClose');
    }
  }, [open, telemetry]);
}

/**
 * Fires layer (dialog) telemetry events for the specified trigger
 *
 * Filters telemetry instructions for the specified trigger and fires all matching events.
 * Used internally by useDialogTelemetry.
 */
function fireLayerTelemetry(
  telemetry: TelemetryInstruction[] | undefined,
  trigger: 'onLayerOpen' | 'onLayerClose',
): void {
  if (!telemetry || telemetry.length === 0) {
    return;
  }

  const instructions = telemetry.filter(
    instruction =>
      instruction.type === 'event' && instruction.trigger === trigger,
  );

  for (const instruction of instructions) {
    if (instruction.type !== 'event') {
      continue;
    }

    trackEvent({
      event_name: instruction.eventName,
      ...instruction.data,
    });
  }
}
